import GoogleMapsMarker from '@assets/icons/google-maps-marker.svg';
import Location from '@assets/icons/location.svg';
import { GOOGLE_MAP_API_KEY } from '@constants/common';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { LanguageSpoken } from '@event/routes/public/EventDetail/EventDetailCard';
import { PublicOnSiteEvent } from '@event/types/event';
import { Accordion, AccordionDetails, AccordionSummary, Divider } from '@mui/material';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const MAP_CENTER = { lat: 60, lng: -120 };

const EventDetailCardOnsite = () => {
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [marker, setMarker] = useState<google.maps.LatLng | null>(null);

    const {
        event: { data: anyEvent, loading: isEventLoading },
    } = usePublicEventContext();
    const event = anyEvent as PublicOnSiteEvent;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAP_API_KEY,
    });

    useEffect(() => {
        if (isLoaded && map && !marker && !isEventLoading) {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { address: `${event?.location},${event.city ? `${event.city},` : ''}${event?.country?.name}` },
                (results, status) => {
                    if (status === google.maps.GeocoderStatus.OK && results) {
                        if (results[0].geometry && results[0].geometry.viewport) {
                            const coordinates = results[0].geometry.location;
                            setMarker(coordinates);
                            map?.setCenter(coordinates);
                            map?.setZoom(16);
                        }
                    } else {
                        map?.setZoom(0);
                        setMarker(null);
                    }
                }
            );
        }
    }, [isLoaded, map, marker, isEventLoading]);

    const onMapLoad = useCallback(function callback(map: google.maps.Map) {
        setMap(map);
    }, []);

    const hasLocationData = useMemo(
        () => event?.location || event?.country || event?.city || event?.custom_location,
        [event]
    );

    return (
        <div className="px-3.5 md:px-0">
            {hasLocationData ? (
                <>
                    <div className="flex justify-between">
                        <div className="flex items-center gap-2">
                            <img src={Location} />
                            <span className="text-sm font-bold">
                                <FormattedMessage id="common.location.uppercase" defaultMessage="LOCATION" />
                            </span>
                        </div>
                        {event.country ? (
                            <img src={`/static/flags/${event.country?.code.toLowerCase()}.svg`} className="w-[49px]" />
                        ) : null}
                    </div>
                    <p className="max-w-[276px] font-normal text-sm mb-5">
                        {event.custom_location ? event.custom_location : event?.location}
                        <br />
                        <span>
                            {event.city ? `${event.city} ${event?.country ? ', ' : ''}` : ''}
                            {event?.country?.name}
                        </span>
                    </p>
                </>
            ) : null}
            {event?.language_spoken ? <LanguageSpoken className="mt-5" /> : null}
            {hasLocationData ? (
                <Accordion className="mb-5" defaultExpanded={false}>
                    <AccordionSummary className="py-2">
                        <div className="flex items-center gap-2">
                            <img src={GoogleMapsMarker} />
                            <a
                                className="text-spanish-blue font-semibold text-xs"
                                href={
                                    marker
                                        ? `https://www.google.com/maps/search/?api=1&query=${marker.lat()},${marker.lng()}`
                                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                              `${event?.location},${event.city ? `${event.city},` : ''}${
                                                  event?.country?.name
                                              }`
                                          )}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <FormattedMessage id="event.showOnGoogleMaps" defaultMessage="Show on Google maps" />
                            </a>
                        </div>
                        <Divider className="w-full absolute bottom-0" />
                    </AccordionSummary>
                    <AccordionDetails>
                        {isLoaded ? (
                            <GoogleMap
                                mapContainerStyle={{
                                    width: '100%',
                                    height: '200px',
                                    borderRadius: 8,
                                }}
                                center={MAP_CENTER}
                                zoom={1}
                                onLoad={onMapLoad}
                                mapContainerClassName="mt-2"
                            >
                                {marker ? <Marker position={marker} /> : null}
                            </GoogleMap>
                        ) : null}
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </div>
    );
};

export { EventDetailCardOnsite };
